// Shared invoice PDF generation pipeline (facade).
//
// Renders the DOM pages of a mounted <Invoice /> (the host element) to a PDF
// blob. Used by:
//   - InvoiceExport.jsx  (download single / ZIP export — portrait)
//   - InvoiceProfile.jsx (Draft Email attachment + Export PDF — portrait)
//   - InterpretationOPIUpload.jsx (Detailed Logs ZIP export — landscape,
//     pinned to the raster engine)
//
// Engines (`engine` option, default "text"):
//   - "text": DOM text-replay real-text engine (P2, shipped) — selectable
//     text, vector fills, small files. See ./pdf/domReplay.js.
//   - "raster": html2canvas capture → JPEG image per page (legacy pipeline,
//     still fully supported as the explicit rollback / OPI pin). See
//     ./pdf/invoicePdfRaster.js. Pass `engine: "raster"` to roll back.
//
// This facade owns the jsPDF instance (letter geometry per orientation) and
// the per-page link annotations, so every engine shares identical geometry
// and link behavior. The host may contain the pages directly or wrap them in
// a single container — see resolvePageElements.

import { paintRasterPages } from "./pdf/invoicePdfRaster";

const LETTER_WIDTH_MM = 215.9;
const LETTER_HEIGHT_MM = 279.4;

/** True when the element is a rendered page shell. */
const isPageElement = (el) => el.classList.contains("InvoiceItem");

/**
 * Resolve the rendered page elements inside a host container. The app
 * mounts the invoice document in three shapes:
 *   (a) the host's direct children ARE the pages (.InvoiceItem) —
 *       InvoiceProfile's ref on <Invoice /> and the OPI Detailed Logs
 *       document root;
 *   (b) a single wrapper child around the <Invoice /> root — InvoiceExport's
 *       hidden container; the pages are that child's top-level .InvoiceItem
 *       elements;
 *   (c) otherwise, a top-level .InvoiceItem scan of the host.
 * @param {HTMLElement} host
 * @returns {HTMLElement[]} page elements in render order (possibly empty)
 */
const resolvePageElements = (host) => {
  const children = Array.from(host.children);

  // (a) Every direct child is already a page.
  if (children.length && children.every(isPageElement)) {
    return children;
  }

  // (b) Exactly one wrapper child (InvoiceExport): use its top-level pages.
  if (children.length === 1) {
    const nested = Array.from(children[0].children).filter(isPageElement);
    if (nested.length) return nested;
  }

  // (c) Fallback: top-level .InvoiceItem scan of the host itself.
  return children.filter(isPageElement);
};

/**
 * Overlay link annotations for anchors (mailto, http/https, tel) on every
 * page — engine-agnostic. Positions are measured in the page's own pixels
 * and scaled to mm with the orientation-aware sheet geometry (same math as
 * the historic pipeline). jsPDF attaches a link to whichever page is current
 * when `pdf.link` runs, so each page is re-selected before its anchors.
 * @param {import("jspdf").jsPDF} pdf
 * @param {HTMLElement[]} pageElements
 * @param {number} pageWidthMm
 * @param {number} pageHeightMm
 */
const paintLinkAnnotations = (pdf, pageElements, pageWidthMm, pageHeightMm) => {
  // Single cheap flag read — set window.__CE_INVOICE_PDF_DEBUG = true in
  // DevTools to get per-page ground truth: which anchors became link
  // annotations, at which rects, plus any zero-size skips.
  const debug =
    typeof window !== "undefined" && window.__CE_INVOICE_PDF_DEBUG;
  const round1 = (value) => Math.round(value * 10) / 10;
  let totalLinks = 0;
  let pagesWithLinks = 0;

  pageElements.forEach((pageElement, index) => {
    const anchors = pageElement.querySelectorAll(
      'a[href^="mailto:"], a[href^="http://"], a[href^="https://"], a[href^="tel:"]',
    );
    if (!anchors.length) return;

    const pageRect = pageElement.getBoundingClientRect();
    const mmPerPxX = pageWidthMm / pageRect.width;
    const mmPerPxY = pageHeightMm / pageRect.height;
    const debugAnchors = [];
    let pageLinks = 0;

    pdf.setPage(index + 1);

    anchors.forEach((anchor) => {
      const rect = anchor.getBoundingClientRect();
      const href = anchor.getAttribute("href");
      if (!href) return;

      const xMm = (rect.left - pageRect.left) * mmPerPxX;
      const yMm = (rect.top - pageRect.top) * mmPerPxY;
      const wMm = rect.width * mmPerPxX;
      const hMm = rect.height * mmPerPxY;

      if (wMm > 0 && hMm > 0) {
        pdf.link(xMm, yMm, wMm, hMm, { url: href });
        totalLinks += 1;
        pageLinks += 1;
        if (debug) {
          debugAnchors.push({
            href,
            left: round1(xMm),
            top: round1(yMm),
            width: round1(wMm),
            height: round1(hMm),
          });
        }
      } else if (debug) {
        // Visible skip diagnostics — no behavior change without the flag.
        debugAnchors.push({
          href,
          left: round1(xMm),
          top: round1(yMm),
          width: round1(wMm),
          height: round1(hMm),
          skipped: "zero-size rect",
        });
      }
    });

    if (pageLinks > 0) pagesWithLinks += 1;
    if (debug && debugAnchors.length) {
      console.log("[invoice-pdf-links]", {
        page: index + 1,
        anchors: debugAnchors,
      });
    }
  });

  if (debug) {
    console.log(
      `[invoice-pdf-links] total annotations added: ${totalLinks} across ${pagesWithLinks} pages`,
    );
  }
};

/**
 * Generate a PDF blob from a rendered invoice host element.
 * @param {HTMLElement} host - element containing the rendered invoice pages
 * @param {Object} [options]
 * @param {string} [options.engine="text"] - "text" (real-text replay, default) or "raster" (legacy html2canvas; explicit rollback / OPI)
 * @param {number} [options.scale=8] - html2canvas capture scale (raster engine only — accepted but ignored by the text engine)
 * @param {(status: string, progress: number) => void} [options.onProgress] - optional progress callback
 * @param {string} [options.orientation="portrait"] - page orientation, "portrait" or "landscape"
 * @returns {Promise<Blob>} the PDF blob
 */
export const generateInvoicePdfBlob = async (host, options = {}) => {
  const {
    engine = "text", // P2: real-text engine is the default; "raster" remains the explicit rollback.
    scale,
    onProgress,
    orientation = "portrait",
  } = options;
  if (!host) throw new Error("Render container missing");

  // Unknown engines fail loudly — never silently fall back to an engine.
  if (engine !== "raster" && engine !== "text") {
    throw new Error(`Unknown invoice PDF engine: "${engine}"`);
  }

  const pageElements = resolvePageElements(host);
  if (!pageElements.length) throw new Error("No pages to export");

  const isLandscape = orientation === "landscape";
  // Letter sheet geometry per orientation. Portrait keeps the historic
  // 215.9 × 279.4mm (array format, unchanged behavior); landscape is the
  // same sheet rotated to 279.4 × 215.9mm, built with the "letter" keyword
  // to avoid jsPDF's array-format/orientation swap ambiguity.
  const pageWidthMm = isLandscape ? LETTER_HEIGHT_MM : LETTER_WIDTH_MM;
  const pageHeightMm = isLandscape ? LETTER_WIDTH_MM : LETTER_HEIGHT_MM;

  const jsPDF = (await import("jspdf")).default;

  const pdf = new jsPDF({
    orientation: isLandscape ? "landscape" : "portrait",
    unit: "mm",
    format: isLandscape ? "letter" : [LETTER_WIDTH_MM, LETTER_HEIGHT_MM],
    // Stream compression pays off for the text engine's vector content; the
    // raster instance stays byte-identical (JPEG streams are already dense).
    ...(engine === "text" ? { compress: true } : {}),
  });

  // Paint the pages with the selected engine. The sheet geometry travels
  // with the call so painting stays identical to the historic pipeline.
  // The text engine (and its font assets) loads on first use only — the
  // raster module is already lazily pulling html2canvas the same way.
  if (engine === "text") {
    const { paintTextPages } = await import("./pdf/domReplay");
    await paintTextPages(pdf, pageElements, {
      onProgress,
      pageWidthMm,
      pageHeightMm,
    });
  } else {
    await paintRasterPages(pdf, pageElements, {
      scale,
      onProgress,
      pageWidthMm,
      pageHeightMm,
    });
  }

  // Link annotations run after painting — engine-agnostic, same math.
  paintLinkAnnotations(pdf, pageElements, pageWidthMm, pageHeightMm);

  return pdf.output("blob");
};

/** Trigger a browser download for a blob. */
export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Cleanup the URL object to free memory
  setTimeout(() => URL.revokeObjectURL(url), 100);
};